import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Checkbox,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useParams } from "react-router-dom";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const InfoItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography fontWeight={600}>
      {value || "-"}
    </Typography>
  </Box>
);

const ReferalSourcesDetails = () => {
  const { id } = useParams();

  const [source, setSource] = useState(null);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");

  const [loadingSource, setLoadingSource] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(true);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  /* ---------------- Fetch Referral Source ---------------- */
  const fetchReferralSource = async () => {
    try {
      setLoadingSource(true);
      const res = await ApiServices.get(`/referral-sources/${id}/`);
      setSource(res?.data || null);
    } catch {
      setSource(null);
      showSnack("Failed to load referral source", "error");
    } finally {
      setLoadingSource(false);
    }
  };

  /* ---------------- Fetch Leads ---------------- */
  const fetchLeads = async () => {
    try {
      setLoadingLeads(true);
      const res = await ApiServices.getwitParams(
        `/referral-leads/?referral_source_id=${id}`
      );

      const data = Array.isArray(res?.data) ? res.data : [];
      setLeads(
        data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
      );
    } catch {
      setLeads([]);
      showSnack("Failed to load referral leads", "error");
    } finally {
      setLoadingLeads(false);
    }
  };

  useEffect(() => {
    fetchReferralSource();
    fetchLeads();
  }, [id]);

  /* ---------------- Search Filter ---------------- */
  const filteredLeads = useMemo(() => {
    if (!search) return leads;
    return leads.filter((l) =>
      l?.customer_name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [leads, search]);

  /* ---------------- Toggle Converted ---------------- */
  const toggleConverted = async (lead) => {
    try {
      await ApiServices.patch(`/referral-leads/${lead.id}/`, {
        is_converted: !lead.is_converted,
      });
      showSnack("Lead updated successfully");
      fetchLeads();
    } catch {
      showSnack("Failed to update lead", "error");
    }
  };

  /* ---------------- Loading ---------------- */
  if (loadingSource) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress />
        <Typography mt={2}>Loading referral source...</Typography>
      </Box>
    );
  }

  if (!source) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h6" color="error">
          Referral source not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* ================= Referral Source Card ================= */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 3, boxShadow: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight={600}>
            Referral Source Profile
          </Typography>

          <Box display="flex" gap={1}>
            <Chip
              label={source?.is_active ? "Active" : "Inactive"}
              color={source?.is_active ? "success" : "error"}
              size="small"
            />
            <Chip
              label={source?.refer_allow ? "Refer Allowed" : "Refer Disabled"}
              color={source?.refer_allow ? "success" : "default"}
              size="small"
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* BASIC INFO */}
          <Grid item xs={12} md={4}>
            <InfoItem label="Name" value={source?.name} />
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoItem label="Email" value={source?.email} />
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoItem label="Phone" value={source?.phone} />
          </Grid>

          {/* WORK INFO */}
          <Grid item xs={12} md={4}>
            <InfoItem label="Company" value={source?.company} />
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoItem label="Department" value={source?.department} />
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoItem label="Position" value={source?.position} />
          </Grid>

          {/* ADDRESS */}
          <Grid item xs={12} md={8}>
            <InfoItem label="Address" value={source?.address} />
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoItem label="ZIP Code" value={source?.zip_code} />
          </Grid>

          {/* BANK INFO */}
          <Grid item xs={12} md={4}>
            <InfoItem
              label="Bank Name"
              value={source?.bank_details?.bank_name}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoItem
              label="Account Number"
              value={source?.bank_details?.account_number}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InfoItem
              label="Routing Number"
              value={source?.bank_details?.routing_number}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ================= Leads Table ================= */}
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom>
          Referred Leads
        </Typography>

        <TextField
          fullWidth
          placeholder="Search by customer name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        {loadingLeads ? (
          <Box textAlign="center" py={3}>
            <CircularProgress />
            <Typography mt={2}>Loading leads...</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Converted</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredLeads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell>{lead.customer_name}</TableCell>
                  <TableCell>{lead.customer_phone}</TableCell>

                  <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                    {lead.service_needed}
                  </TableCell>

                  <TableCell sx={{ fontStyle: "italic" }}>
                    {lead.notes || "-"}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={lead.payment_status}
                      color={
                        lead.payment_status === "PAID"
                          ? "success"
                          : lead.payment_status === "PARTIAL"
                          ? "warning"
                          : "default"
                      }
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    <Checkbox
                      checked={!!lead.is_converted}
                      onChange={() => toggleConverted(lead)}
                    />
                  </TableCell>

                  <TableCell>
                    {new Date(lead.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}

              {filteredLeads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No leads found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferalSourcesDetails;
